import { useState, useEffect, lazy, Suspense, Fragment, type ComponentType } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSession } from './lib/auth-client';
import { nextOrDefault, signInHref } from './lib/redirect-target';
import { LocaleRouteSync } from './lib/locale-sync';
import { PREFIXED_LOCALES } from './lib/locale-path';
import type { PublicPage, DocsPage } from './lib/site-routes';

// Auth pages
import { SignIn } from './pages/auth/SignIn';
import { SignUp } from './pages/auth/SignUp';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { VerifyToken } from './pages/auth/VerifyToken';

// Dashboard pages
import { DashboardLayout } from './components/layout/DashboardLayout';
import { Dashboard } from './pages/dashboard/Dashboard';
import { Profile } from './pages/dashboard/Profile';
import { Security } from './pages/dashboard/Security';
import { Billing } from './pages/dashboard/Billing';
import { Usage } from './pages/dashboard/Usage';

// Public pages
import { LandingLayout } from './components/layout/LandingLayout';
import { DocsLayout } from './components/layout/DocsLayout';
import { Landing } from './pages/public/Landing';
import { Scenarios } from './pages/public/Scenarios';
import { Providers } from './pages/public/Providers';
import { LocalAI } from './pages/public/LocalAI';
import { Privacy as PublicPrivacy } from './pages/public/Privacy';
import { Download } from './pages/public/Download';
import { DocsHome } from './pages/docs/DocsHome';
import { WindowsInstall } from './pages/docs/WindowsInstall';
import { LinuxInstall } from './pages/docs/LinuxInstall';
import { MacOSInstall } from './pages/docs/MacOSInstall';
import { SupportedSites } from './pages/docs/SupportedSites';
import { AIProviders } from './pages/docs/AIProviders';
import { ParticipantAudio } from './pages/docs/ParticipantAudio';
import { SubtitleMode } from './pages/docs/SubtitleMode';
import { FeatureCaptionSharing } from './pages/docs/tutorials/FeatureCaptionSharing';
import { FeatureTranslationModes } from './pages/docs/tutorials/FeatureTranslationModes';
import { FeatureVirtualMicrophone } from './pages/docs/tutorials/FeatureVirtualMicrophone';
import { FeatureTextOnly } from './pages/docs/tutorials/FeatureTextOnly';
import { FeatureDisplay } from './pages/docs/tutorials/FeatureDisplay';
import { FeaturePronunciationReadings } from './pages/docs/tutorials/FeaturePronunciationReadings';
import { FeatureExport } from './pages/docs/tutorials/FeatureExport';
import { FeatureReplay } from './pages/docs/tutorials/FeatureReplay';
import { FeatureNoiseSuppression } from './pages/docs/tutorials/FeatureNoiseSuppression';
import { FeaturePassthrough } from './pages/docs/tutorials/FeaturePassthrough';
import { FeatureSwitchDevices } from './pages/docs/tutorials/FeatureSwitchDevices';
import { FeatureDiagnosticLogs } from './pages/docs/tutorials/FeatureDiagnosticLogs';
import { FeatureInterfaceLanguage } from './pages/docs/tutorials/FeatureInterfaceLanguage';
import { PrivacyPolicy } from './pages/docs/PrivacyPolicy';
import { DeleteAccount } from './pages/docs/DeleteAccount';
import { TermsOfService } from './pages/docs/TermsOfService';
import { CommerceDisclosure } from './pages/docs/CommerceDisclosure';

// Dashboard pages - Feedback
import { Feedback } from './pages/dashboard/Feedback';

// Tutorial pages - Platform tutorials
import { ZoomTutorial } from './pages/docs/tutorials/ZoomTutorial';
import { GoogleMeetTutorial } from './pages/docs/tutorials/GoogleMeetTutorial';
import { MicrosoftTeamsTutorial } from './pages/docs/tutorials/MicrosoftTeamsTutorial';
import { DiscordTutorial } from './pages/docs/tutorials/DiscordTutorial';
import { SlackTutorial } from './pages/docs/tutorials/SlackTutorial';
import { JitsiTutorial } from './pages/docs/tutorials/JitsiTutorial';
import { WherebyTutorial } from './pages/docs/tutorials/WherebyTutorial';
import { GatherTutorial } from './pages/docs/tutorials/GatherTutorial';

// Tutorial pages - AI Provider setups
import { OpenAISetup } from './pages/docs/tutorials/OpenAISetup';
import { GeminiSetup } from './pages/docs/tutorials/GeminiSetup';
import { PalabraAISetup } from './pages/docs/tutorials/PalabraAISetup';
import { VolcengineAST2Setup } from './pages/docs/tutorials/VolcengineAST2Setup';
import { SonioxSetup } from './pages/docs/tutorials/SonioxSetup';
import { QwenSetup } from './pages/docs/tutorials/QwenSetup';
import { KizunaAIQuickStart } from './pages/docs/tutorials/KizunaAIQuickStart';
import { TwoWayMeetingTutorial } from './pages/docs/tutorials/TwoWayMeetingTutorial';
import { UnderstandOthersTutorial } from './pages/docs/tutorials/UnderstandOthersTutorial';
import { BeUnderstoodTutorial } from './pages/docs/tutorials/BeUnderstoodTutorial';
import { SubtitleMySpeechTutorial } from './pages/docs/tutorials/SubtitleMySpeechTutorial';
import { TwoWaySubtitlesTutorial } from './pages/docs/tutorials/TwoWaySubtitlesTutorial';
import { KizunaFaceToFaceTutorial } from './pages/docs/tutorials/KizunaFaceToFaceTutorial';
import { KizunaVoicesGuide } from './pages/docs/tutorials/KizunaVoicesGuide';
import { KizunaVocabularyGuide } from './pages/docs/tutorials/KizunaVocabularyGuide';
import { KizunaRegionGuide } from './pages/docs/tutorials/KizunaRegionGuide';
import { KizunaLatencyGuide } from './pages/docs/tutorials/KizunaLatencyGuide';
import { KizunaBillingGuide } from './pages/docs/tutorials/KizunaBillingGuide';
import { LocalInferenceSetup } from './pages/docs/tutorials/LocalInferenceSetup';
import { LocalModels } from './pages/docs/LocalModels';
import { RealtimeAPITester } from './pages/docs/tutorials/RealtimeAPITester';

// Standalone pages
import { UninstallFeedback } from './pages/UninstallFeedback';

// Admin console (lazy chunk — admin code + auth logic stay out of the normal bundle)
const AdminApp = lazy(() => import('./admin/AdminApp'));

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (!isPending) {
      setInitialLoad(false);
    }
  }, [isPending]);

  // Only show loading on initial load, not on refetch (e.g., tab switch)
  if (isPending && initialLoad) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!session) {
    // Carry the destination through the sign-in form. Without it every deep
    // link into the dashboard finished on /dashboard instead — including
    // /dashboard/billing, which is where the app's "Top up" button points and
    // where anyone arriving with a stale one-time token ends up.
    return <Navigate to={signInHref(location.pathname, location.search)} replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (!isPending) {
      setInitialLoad(false);
    }
  }, [isPending]);

  // Only show loading on initial load, not on refetch (e.g., tab switch)
  if (isPending && initialLoad) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (session) {
    // Preserve an explicit redirect-back target (e.g. /sign-in?next=/admin/...)
    // instead of always sending an authenticated visitor to the dashboard.
    const target = nextOrDefault(new URLSearchParams(location.search).get('next'));
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}

function WalletRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/dashboard/billing', search: location.search }} replace />;
}


// Public site and docs pages. The keys are site-routes.ts's page lists, so a
// page cannot be routed without being listed there (and prerendered and put
// in the sitemap), or listed without a component.
const PUBLIC_ELEMENTS: Record<PublicPage, ComponentType> = {
  '': Landing,
  'scenarios': Scenarios,
  'providers': Providers,
  'local-ai': LocalAI,
  'privacy': PublicPrivacy,
  'download': Download,
};

const DOCS_ELEMENTS: Record<DocsPage, ComponentType> = {
  '': DocsHome,
  'install/windows': WindowsInstall,
  'install/linux': LinuxInstall,
  'install/macos': MacOSInstall,
  'supported-sites': SupportedSites,
  'ai-providers': AIProviders,
  'features/participant-audio': ParticipantAudio,
  'features/subtitle-mode': SubtitleMode,
  'features/caption-sharing': FeatureCaptionSharing,
  'features/translation-modes': FeatureTranslationModes,
  'features/virtual-microphone': FeatureVirtualMicrophone,
  'features/text-only': FeatureTextOnly,
  'features/display': FeatureDisplay,
  'features/pronunciation-readings': FeaturePronunciationReadings,
  'features/export': FeatureExport,
  'features/replay': FeatureReplay,
  'features/noise-suppression': FeatureNoiseSuppression,
  'features/passthrough': FeaturePassthrough,
  'features/switch-devices': FeatureSwitchDevices,
  'features/diagnostic-logs': FeatureDiagnosticLogs,
  'features/interface-language': FeatureInterfaceLanguage,
  'privacy': PrivacyPolicy,
  'delete-account': DeleteAccount,
  'terms': TermsOfService,
  'commerce': CommerceDisclosure,
  'tutorials/zoom': ZoomTutorial,
  'tutorials/google-meet': GoogleMeetTutorial,
  'tutorials/microsoft-teams': MicrosoftTeamsTutorial,
  'tutorials/discord': DiscordTutorial,
  'tutorials/slack': SlackTutorial,
  'tutorials/jitsi': JitsiTutorial,
  'tutorials/whereby': WherebyTutorial,
  'tutorials/gather': GatherTutorial,
  'tutorials/openai-setup': OpenAISetup,
  'tutorials/gemini-setup': GeminiSetup,
  'tutorials/palabraai-setup': PalabraAISetup,
  'tutorials/volcengine-ast2-setup': VolcengineAST2Setup,
  'tutorials/soniox-setup': SonioxSetup,
  'tutorials/qwen-setup': QwenSetup,
  'kizuna-ai/quick-start': KizunaAIQuickStart,
  'kizuna-ai/two-way-meeting': TwoWayMeetingTutorial,
  'kizuna-ai/understand-others': UnderstandOthersTutorial,
  'kizuna-ai/be-understood': BeUnderstoodTutorial,
  'kizuna-ai/subtitle-my-speech': SubtitleMySpeechTutorial,
  'kizuna-ai/two-way-subtitles': TwoWaySubtitlesTutorial,
  'kizuna-ai/face-to-face': KizunaFaceToFaceTutorial,
  'kizuna-ai/voices': KizunaVoicesGuide,
  'kizuna-ai/vocabulary': KizunaVocabularyGuide,
  'kizuna-ai/region': KizunaRegionGuide,
  'kizuna-ai/latency': KizunaLatencyGuide,
  'kizuna-ai/billing': KizunaBillingGuide,
  'tutorials/local-inference-setup': LocalInferenceSetup,
  'local-models': LocalModels,
  'tutorials/realtime-api-tester': RealtimeAPITester,
};

/** The public site and docs route trees under one locale prefix ('' for English). */
function siteRoutes(prefix: string) {
  const page = (pages: Record<string, ComponentType>) =>
    Object.entries(pages).map(([path, Page]) =>
      path ? <Route key={path} path={path} element={<Page />} /> : <Route key="index" index element={<Page />} />,
    );
  return (
    <Fragment key={prefix || 'en'}>
      <Route path={prefix || '/'} element={<LandingLayout />}>
        {page(PUBLIC_ELEMENTS)}
      </Route>
      <Route path={`${prefix}/docs`} element={<DocsLayout />}>
        {page(DOCS_ELEMENTS)}
      </Route>
    </Fragment>
  );
}

export default function App() {
  // index.html hid a prerendered page that belongs to another path; the real
  // route is on screen now.
  useEffect(() => {
    document.documentElement.removeAttribute('data-spa-shell');
  }, []);

  return (
    <>
    <LocaleRouteSync />
    <Routes>
      {/* Public site and docs: English unprefixed, other locales under /ja, /zh, ... */}
      {siteRoutes('')}
      {PREFIXED_LOCALES.map((locale) => siteRoutes(`/${locale}`))}

      {/* Public auth routes */}
      <Route
        path="/sign-in"
        element={
          <PublicRoute>
            <SignIn />
          </PublicRoute>
        }
      />
      <Route
        path="/sign-up"
        element={
          <PublicRoute>
            <SignUp />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/verify" element={<VerifyToken />} />

      {/* Standalone public pages */}
      <Route path="/uninstall-feedback" element={<UninstallFeedback />} />

      {/* Protected dashboard routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<Profile />} />
        <Route path="security" element={<Security />} />
        <Route path="billing" element={<Billing />} />
        <Route path="usage" element={<Usage />} />
        {/* Kept because Stripe bakes success_url into each checkout session AT
            CREATION TIME: anyone mid-payment when this deploys comes back to
            /dashboard/wallet?success=true. The SEARCH MUST SURVIVE — Billing
            reads `success=true` to request `fresh=1`, which is what reads past
            the KV cache. Without it the balance shown for up to a minute after
            paying is the pre-payment one, while the ledger beside it already
            shows the payment. Bookmarks are the lesser reason. */}
        <Route path="wallet" element={<WalletRedirect />} />
        <Route path="feedback" element={<Feedback />} />
      </Route>

      {/* Admin console (gated, lazy-loaded; falls through to catch-all only if not matched) */}
      <Route
        path="/admin/*"
        element={
          <Suspense
            fallback={
              <div className="loading-screen">
                <div className="loading-spinner" />
              </div>
            }
          >
            <AdminApp />
          </Suspense>
        }
      />

      {/* Fallback redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
